import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageOpen, Search, AlertTriangle } from 'lucide-react';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import ReportExportButton from '@/components/reports/ReportExportButton';

type AlertMode = 'alerts' | 'negative' | 'zero' | 'below' | 'reorder';

const MODE_OPTIONS: { id: AlertMode; label: string; hint: string }[] = [
  { id: 'alerts', label: 'All alerts', hint: 'Negative, zero, or below reorder level' },
  { id: 'negative', label: 'Negative stock', hint: 'Only items with stock below zero' },
  { id: 'zero', label: 'Zero stock', hint: 'Items with exactly 0 on hand' },
  { id: 'below', label: 'Below threshold', hint: 'Enter a number — show stock less than that' },
  { id: 'reorder', label: 'Below reorder', hint: 'At or under each item’s reorder level' },
];

function statusClass(status: string) {
  if (status === 'Negative') return 'bg-rose-50 text-rose-700 border-rose-200';
  if (status === 'Zero') return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
}

export default function StockAlertsSection() {
  const [mode, setMode] = useState<AlertMode>('alerts');
  const [threshold, setThreshold] = useState('5');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'Product'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Product' });
      return res.data.message || [];
    },
  });

  const thresholdNum = threshold.trim() === '' ? undefined : Number(threshold);
  const canFetch = mode !== 'below' || (thresholdNum != null && !Number.isNaN(thresholdNum));

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['stockAlerts', mode, threshold, category, search],
    enabled: canFetch,
    queryFn: async () => {
      const res = await posApi.getStockAlertsReport({
        mode,
        threshold: mode === 'below' ? thresholdNum : undefined,
        category: category || undefined,
        item: search.trim() || undefined,
      });
      return res.data.message as {
        rows: any[];
        summary: {
          count: number;
          negative_count: number;
          zero_count: number;
          positive_low_count: number;
          total_qty: number;
        };
      };
    },
  });

  const rows = data?.rows || [];
  const summary = data?.summary;

  const totalsLine = useMemo(() => {
    if (!summary) return null;
    return `${summary.count} item(s) · ${summary.negative_count} negative · ${summary.zero_count} zero · ${summary.positive_low_count} low positive`;
  }, [summary]);

  const handleExport = () => {
    if (!rows.length) return;
    const headers = [
      'Status',
      'Item Code',
      'Item Name',
      'Variant',
      'Category',
      'UOM',
      'Stock',
      'Reorder Level',
      'Cost',
      'Sale Price',
    ];
    const csvRows = rows.map((r) =>
      [
        r.status,
        r.item_code,
        `"${(r.item_name || '').replace(/"/g, '""')}"`,
        `"${(r.variant_name || '').replace(/"/g, '""')}"`,
        `"${(r.category_name || '').replace(/"/g, '""')}"`,
        r.unit_of_measurement || '',
        r.available_stock,
        r.reorder_level,
        r.cost_price,
        r.sale_price,
      ].join(','),
    );
    const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock_alerts_${mode}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setMode(opt.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                mode === opt.id
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              title={opt.hint}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {mode === 'below' && (
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Show stock less than
              </label>
              <input
                type="number"
                min="0"
                step="1"
                className="input-field"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
          )}
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              className="input-field"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All categories</option>
              {(categoriesData || []).map((c: any) => (
                <option key={c.name} value={c.name}>
                  {c.category_name || c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input-field pl-9"
                placeholder="Item name, code or barcode"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>

        <p className="text-xs text-gray-500">
          {MODE_OPTIONS.find((m) => m.id === mode)?.hint}
          {totalsLine ? ` · ${totalsLine}` : ''}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          Failed to load stock alerts.
        </div>
      )}

      {!canFetch && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Enter a threshold number to list items below that stock level.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap min-w-[880px]">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Variant</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Stock</th>
                <th className="px-4 py-3 font-medium text-right">Reorder</th>
                <th className="px-4 py-3 font-medium text-right">Cost</th>
                <th className="px-4 py-3 font-medium text-right">Sale price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading || isFetching ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading stock alerts…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <PackageOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="font-medium text-gray-700">No matching stock items</p>
                    <p className="text-xs mt-1">Try another filter or a higher threshold.</p>
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={`${r.item_code}-${r.variant_name || ''}-${idx}`} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${statusClass(
                          r.status,
                        )}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.item_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.item_code}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.variant_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.category_name || '—'}</td>
                    <td
                      className={`px-4 py-3 text-right font-bold ${
                        r.available_stock < 0
                          ? 'text-rose-600'
                          : r.available_stock === 0
                            ? 'text-slate-600'
                            : 'text-amber-700'
                      }`}
                    >
                      {r.available_stock}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{r.reorder_level ?? '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatMoney(r.cost_price)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatMoney(r.sale_price)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
